import {
	applyFilter,
	type ArchiveFilter,
	archiveItems,
	type ArchiveItem,
	currentStreak,
	type DateKey,
	filterRuleNames,
	findStack,
	isFiltered,
	isNewPlayer,
	type Lookup,
	MAX_SAVED_STACKS,
	newStackId,
	nextInStack,
	numberForDate,
	parseQuery,
	practiceItems,
	type QueryWord,
	relaxations,
	removeFromQuery,
	removeKeyFromQuery,
	resolveLookup,
	type SavedStack,
	savedStackDef,
	SORT_KEYS,
	type SortKey,
	type StackDef,
	type StackId,
	stackItems,
	stacksFor,
} from '@arrow/game-engine';
import { DEFAULT_LEVEL, type Level, LEVELS, type Puzzle, type PuzzleType, puzzleShape } from '@arrow/puzzle-schema';
import { type ChangeEvent, type MouseEvent, type ReactNode, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { MEDIA } from '../app/breakpoints.ts';
import { setPageTitle } from '../app/page-title.ts';
import { type PlayContext, type PlayParams, playPath } from '../app/play-context.ts';
import type { Prefs } from '../app/prefs.ts';
import { goUp, navigate } from '../app/router.tsx';
import { AppMenu } from '../components/AppMenu.tsx';
import { BackPill } from '../components/BackPill.tsx';
import { BrandRow } from '../components/BrandRow.tsx';
import { Icon, SeasonIcon, Wedge } from '../components/icons.tsx';
import { ErrorState, Pending, Skeleton, SkeletonRows } from '../components/States.tsx';
import { TabBar } from '../components/TabBar.tsx';
import { useDevMode } from '../dev/mode.ts';
import { type StackEventId, track } from '../platform/analytics.ts';
import { type Persistence, slotFor } from '../storage/persistence.ts';
import { usePlayData } from './play/archive-data.ts';
import { type BuilderDraft, StackBuilder } from './play/Builder.tsx';
import { describeFilter, filterTokens, itemName, itemPath, longDate, plural, relaxLabel, SORT_NAME, type Token } from './play/model.ts';
import { askedWords, JumpCard, NewStackTile, NoResults, NotUnderstood, PAGE, Rows, StackTile } from './play/parts.tsx';
import { useSheetOpeners } from '../ui/index.ts';
import { SpecialPoster, SpecialTile } from './play/Special.tsx';
import './play/play.css';
import { LEVEL_NAME } from './today-model.ts';

const XS = (
	<svg viewBox="0 0 12 12" aria-hidden="true">
		<path d="M2 2l8 8M10 2l-8 8" />
	</svg>
);
const LENS = (
	<svg viewBox="0 0 24 24" className="find-lens" aria-hidden="true">
		<circle cx="10.5" cy="10.5" r="6.5" />
		<path d="M15.5 15.5L21 21" />
	</svg>
);

/** A new stack starts as everything not done yet. */
const NEW_STACK_FILTER: ArchiveFilter = { levels: [], status: ['new', 'started'], size: 'any', range: 'all', weekday: null, month: null, sort: 'new' };

/**
 * Each Play address's place, so "‹ Play" lands where you were: how far it was scrolled, how
 * many rows "Show more" had opened (else the scroll lands past the end), and the row you
 * left from, which gets focus back (QA2-3).
 */
const scrollAt = new Map<string, number>();
const shownAt = new Map<string, number>();
const leftFrom = new Map<string, string>();
/** A collection opened afresh starts at its top. */
function forgetPlace(path: string): void {
	scrollAt.delete(path);
	shownAt.delete(path);
	leftFrom.delete(path);
}

/** How analytics names a stack: a built-in's id, or `custom` (never a player-made stack's id or name). */
const stackEvent = (s: StackDef): { stack: StackEventId; custom?: true } => (s.mine ? { stack: 'custom', custom: true } : { stack: s.id as StackId });

function useWide(): boolean {
	const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(MEDIA.columns).matches === true);
	useEffect(() => {
		const m = window.matchMedia?.(MEDIA.columns);
		if (!m) return;
		const on = () => setWide(m.matches);
		m.addEventListener?.('change', on);
		return () => m.removeEventListener?.('change', on);
	}, []);
	return wide;
}

interface PlayProps {
	todayKey: DateKey;
	persistence: Persistence;
	/** The remembered level (UX-059's "Play Medium ›" for today's number). */
	prefs: Prefs;
	/** This device's first day (the platform's first-seen date): what "missed" counts from. */
	firstSeen: DateKey | null;
	params?: PlayParams | undefined;
}

/**
 * Play (docs/mockups/play-2.html, Direction F "Stacks" with D's search box): every past
 * Sparrow, back to No. 1, never today's. A search box that takes a number or a date
 * straight to that day and reads words as filters, then the stacks (saved filters with
 * counts, ▶ to play the top one). Opening a stack lists it with its rules, count and sort.
 * On desktop the stacks are a rail beside the open one. What's open lives in the address
 * (`/play?stack=missed&sort=old`, `/play?q=hard+october`). Nothing here touches the streak.
 */
export function Play({ todayKey, persistence, prefs, firstSeen, params }: PlayProps) {
	const devMode = useDevMode();
	const [attempt, setAttempt] = useState(0);
	const data = usePlayData(todayKey, attempt);
	const wide = useWide();
	const stackParam = params?.stack ?? null;
	const sortParam = params?.sort ?? null;
	const qParam = params?.q ?? '';
	const [text, setText] = useState(qParam);
	const here = playPath({ stack: stackParam, q: qParam || null, sort: sortParam });
	const [shown, setShown] = useState(() => shownAt.get(here) ?? PAGE);
	const showMore = () => {
		const next = shown + PAGE;
		shownAt.set(here, next);
		setShown(next);
	};
	const inputRef = useRef<HTMLInputElement>(null);
	const newPlayer = isNewPlayer(todayKey, firstSeen);
	const [saved, setSaved] = useState<SavedStack[]>(() => persistence.myStacks());
	const mine = useMemo(() => saved.map(savedStackDef), [saved]);
	// The builder's draft, and which button opened it (its Radix trigger: ui/openers.tsx).
	const [builder, setBuilder] = useState<(BuilderDraft & { from: string }) | null>(null);
	// The builder's sheet (ui/Sheet) owns its history entry, so back closes it.
	const closeBuilder = () => setBuilder(null);
	// Where focus goes once the page has redrawn (a new stack's tile; the title after a delete).
	const [focusAfter, setFocusAfter] = useState<string | null>(null);
	const full = saved.length >= MAX_SAVED_STACKS;

	// Back / forward to another search: the box follows the address.
	useEffect(() => {
		if (qParam.trim() !== text.trim()) setText(qParam);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [qParam]);

	useEffect(() => {
		track('play_viewed', { newPlayer });
		// Once per visit to the page.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const ready = data.status === 'ready' ? data : null;
	const lists = useMemo(() => {
		if (!ready) return null;
		const local = { stats: persistence.loadStats(), progress: persistence.savedProgress() };
		return {
			dailies: archiveItems(ready.archive.days, local, todayKey, firstSeen),
			practice: practiceItems(ready.practice, local),
		};
	}, [ready, persistence, todayKey, firstSeen]);

	const stacks = useMemo(() => stacksFor(newPlayer, mine), [newPlayer, mine]);
	const counts = useMemo(() => {
		const out = new Map<string, ArchiveItem[]>();
		if (lists) for (const s of stacks) out.set(s.id, stackItems(s, lists.dailies, lists.practice));
		return out;
	}, [lists, stacks]);

	const query = text.trim();
	const parsed = useMemo(() => parseQuery(query, todayKey), [query, todayKey]);
	const openStack: StackDef | null = findStack(stackParam, mine);

	// The tab's title follows the page's: an open collection names itself ("Missed · Play · Sparrow", QA4-13).
	const titled = openStack && !query ? openStack.name : null;
	useEffect(() => setPageTitle(titled ? `${titled} · Play` : 'Play'), [titled]);

	// A stack that isn't there (another device's, a typo): the stacks home. One just deleted
	// is already on its way back there.
	// Said once on the collections, so a bookmark doesn't seem to have vanished without a word (QA2-15).
	const deleted = useRef<string | null>(null);
	const [gone, setGone] = useState(false);
	useEffect(() => {
		if (stackParam && !openStack && deleted.current !== stackParam) {
			setGone(true);
			navigate(playPath({ q: qParam || null }), { replace: true });
		}
	}, [stackParam, openStack, qParam]);
	useEffect(() => {
		if ((stackParam && openStack) || qParam) setGone(false);
	}, [stackParam, openStack, qParam]);

	useEffect(() => {
		if (!focusAfter) return;
		const el = document.getElementById(focusAfter);
		if (!el) return;
		el.focus({ preventScroll: false });
		setFocusAfter(null);
	});
	// Desktop always has a stack open beside the rail: the one asked for, else the most useful.
	const deskStack = openStack ?? defaultStack(stacks, counts);
	const shownStack = query ? null : wide ? deskStack : openStack;

	// The list in view (a stack, or a search's results), in its order.
	const sort: SortKey | null = sortParam;
	const stackList = useMemo(
		() => (shownStack && lists ? stackItems(shownStack, lists.dailies, lists.practice, sort ?? undefined) : []),
		[shownStack, lists, sort],
	);
	const searchFilter = useMemo(() => (parsed.kind === 'words' ? { ...parsed.filter, sort: sort ?? parsed.filter.sort } : null), [parsed, sort]);
	const results = useMemo(() => (searchFilter && lists ? applyFilter(lists.dailies, searchFilter) : []), [searchFilter, lists]);
	const lookup =
		ready && (parsed.kind === 'number' || parsed.kind === 'date') ? resolveLookup(parsed, ready.archive.days, ready.archive.earliest, todayKey) : null;
	const todayNumber = ready?.archive.earliest ? numberForDate(todayKey, ready.archive.earliest) : null;
	// Today's puzzle from a search: the level last played, else the next one not solved yet
	// (after it, then round), else its result (QA2-14).
	const todayPick = (): { level: Level; verb: 'Play' | 'Continue' | 'See result' } => {
		const remembered = prefs.lastLevel() ?? DEFAULT_LEVEL;
		const at = LEVELS.indexOf(remembered);
		const order = [...LEVELS.slice(at), ...LEVELS.slice(0, at)];
		const status = (level: Level) => persistence.resumeInfo(slotFor({ kind: 'daily', date: todayKey, level })).status;
		const open = order.find((l) => status(l) !== 'completed');
		if (!open) return { level: remembered, verb: 'See result' };
		return { level: open, verb: status(open) === 'started' ? 'Continue' : 'Play' };
	};

	// A fresh list starts a page in (one you've been back to, as far as you'd opened it).
	const view = query ? `q:${query}:${sort}` : `s:${shownStack?.id}:${sort}`;
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => setShown(shownAt.get(here) ?? PAGE), [view]);

	// The address: the box writes `q` as you type (replacing, so back isn't a keystroke trail).
	const onType = (e: ChangeEvent<HTMLInputElement>) => {
		const v = e.target.value;
		setText(v);
		const q = v.trim();
		navigate(playPath(q ? { q, sort: sortParam } : { stack: wide ? stackParam : null }), { replace: true });
	};
	const setQuery = (v: string) => {
		setText(v);
		navigate(playPath(v ? { q: v, sort: sortParam } : {}), { replace: true });
		inputRef.current?.focus();
	};

	// A settled search is counted: its kind and how many it found, never the words.
	const searchKind = parsed.kind === 'number' || parsed.kind === 'date' || parsed.kind === 'words' ? parsed.kind : null;
	const found = parsed.kind === 'words' ? results.length : lookup?.kind === 'day' ? lookup.day.levels.length : 0;
	const unknownWords = parsed.kind === 'words' ? parsed.unknown.length : 0;
	useEffect(() => {
		if (!searchKind || !lists) return;
		const t = window.setTimeout(() => {
			track('archive_searched', { kind: searchKind, results: found, unknownWords });
			if (found === 0) track('archive_no_results', { kind: searchKind });
		}, 1000);
		return () => window.clearTimeout(t);
	}, [query, searchKind, found, unknownWords, lists]);

	// Scroll: remembered per address, given back when you come back to it, with focus on the
	// row you left from (the page's heading would be a screen away from it).
	const scroller = useRef<HTMLDivElement>(null);
	useLayoutEffect(() => {
		const el = scroller.current;
		if (!el || !lists) return;
		el.scrollTop = scrollAt.get(here) ?? 0;
		const href = leftFrom.get(here);
		if (href) [...el.querySelectorAll<HTMLAnchorElement>('a.row')].find((a) => a.getAttribute('href') === href)?.focus({ preventScroll: true });
		// Only when the list first draws, or the view changes.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [!!lists, view]);
	const onScroll = () => {
		if (scroller.current) scrollAt.set(here, scroller.current.scrollTop);
	};
	// Leaving by a row: remember which, for the way back.
	const onRowClick = (e: MouseEvent) => {
		const row = e.target instanceof Element ? e.target.closest('a.row') : null;
		const href = row?.getAttribute('href');
		if (href) leftFrom.set(here, href);
		else leftFrom.delete(here);
	};

	const streak = currentStreak(persistence.loadStats(), todayKey);

	const openIt = (s: StackDef) => {
		track('stack_opened', stackEvent(s));
		forgetPlace(playPath({ stack: s.id }));
		navigate(playPath({ stack: s.id }), { replace: wide });
		// A phone swaps the page: focus goes to the collection's title, not lost to the body.
		// (Desktop keeps it on the rail link, which stays.)
		if (!wide) setFocusAfter('stack-title');
	};
	const playFrom = (s: StackDef, list: readonly ArchiveItem[], item: ArchiveItem | null, ctxSort: SortKey | null) => {
		if (!item) return;
		const position = list.findIndex((x) => x.key === item.key) + 1;
		track('stack_played', { ...stackEvent(s), position, ...(item.daysAgo !== null ? { daysAgo: item.daysAgo } : {}) });
		navigate(itemPath(item, { from: 'stack', stack: s.id, q: null, sort: ctxSort }));
	};

	// ---- Your own stacks: make, edit, delete --------------------------------------------
	const store = (list: SavedStack[]) => {
		setSaved(list);
		persistence.saveMyStacks(list);
	};
	const newStack = (from: string, filter?: ArchiveFilter) => setBuilder({ from, mode: 'new', name: null, filter: filter ?? { ...NEW_STACK_FILTER, status: [...NEW_STACK_FILTER.status] } });
	const editStack = (s: StackDef) => setBuilder({ from: `edit:${s.id}`, mode: 'edit', id: s.id, name: s.name, filter: s.filter });
	const onSave = (name: string, filter: ArchiveFilter) => {
		const draft = builder;
		if (!draft) return;
		track('stack_saved', { rules: filterRuleNames(filter), custom: true, edited: draft.mode === 'edit' });
		setBuilder(null);
		if (draft.mode === 'edit' && draft.id) {
			store(saved.map((x) => (x.id === draft.id ? { id: x.id, name, filter } : x)));
			return;
		}
		if (full) return;
		const id = newStackId(
			saved.map((x) => x.id),
			Date.now(),
		);
		store([...saved, { id, name, filter }]);
		forgetPlace(playPath({ stack: id }));
		if (query) setText('');
		// What you usually want next is the collection you just made: open it (UX-057).
		navigate(playPath({ stack: id }), { replace: wide });
		setFocusAfter('stack-title');
	};
	const onDelete = () => {
		const id = builder?.id;
		setBuilder(null);
		if (!id) return;
		track('stack_deleted', { custom: true });
		deleted.current = id;
		store(saved.filter((x) => x.id !== id));
		setFocusAfter('play-title');
		if (wide) navigate(playPath({}), { replace: true });
		else goUp('/play');
	};

	// The builder opens inside the root of the button that opened it (New collection, Save as a
	// collection, Edit: each its Radix trigger), or at the page's foot if that button has gone.
	const builders = useSheetOpeners(builder?.from ?? null, () =>
		builder && lists ? <StackBuilder draft={builder} items={lists.dailies} newPlayer={newPlayer} onClose={closeBuilder} onSave={onSave} onDelete={onDelete} /> : null,
	);

	// The placeholder is the hint on screen; a screen reader hears the hint in full as the
	// box's description (UX-054), since a placeholder isn't reliably read.
	const searchBox = (
		<div className="find-wrap">
			<label className="find" htmlFor="play-find">
				<span className="sr-only">Find a Sparrow</span>
				{LENS}
				<input
					ref={inputRef}
					id="play-find"
					className="field field--search"
					type="search"
					enterKeyHint="search"
					autoComplete="off"
					spellCheck={false}
					placeholder="Search: 17, 17 Oct, hard…"
					aria-describedby={text ? undefined : 'play-find-hint'}
					value={text}
					onChange={onType}
				/>
			</label>
			{text && (
				<button type="button" className="find-clear" aria-label="Clear the search" onClick={() => setQuery('')}>
					{XS}
				</button>
			)}
			{!text && (
				<p className="sr-only" id="play-find-hint">
					Type a number or date to go to that day, or words like hard, Sunday or October.
				</p>
			)}
		</div>
	);

	const sortSelect = (value: SortKey, onChange: (s: SortKey) => void) => (
		<label className="sortw">
			<span className="sr-only">Order</span>
			<select className="field" value={value} onChange={(e) => onChange(e.target.value as SortKey)}>
				{SORT_KEYS.map((k) => (
					<option key={k} value={k}>
						{SORT_NAME[k]}
					</option>
				))}
			</select>
		</label>
	);

	// ---- The search's answer ------------------------------------------------------------
	// Nothing in the box was a word Sparrow knows: no list (all of them would look like a
	// match), only the message and a few words to try (UX-055).
	const notUnderstood = parsed.kind === 'words' && parsed.words.length === 0 && parsed.unknown.length > 0;
	const searchView = () => {
		if (!lists || !ready) return null;
		if ((parsed.kind === 'number' || parsed.kind === 'date') && lookup) {
			const ctx: PlayContext = { from: 'lookup', stack: null, q: query, sort: null };
			return (
				<JumpCard
					lookup={lookup}
					asked={askedWords(parsed)}
					items={lists.dailies}
					todayNumber={todayNumber}
					today={todayPick()}
					ctx={ctx}
					path={(it) => itemPath(it, ctx)}
					onOpen={() => undefined}
				/>
			);
		}
		if (parsed.kind !== 'words' || !searchFilter) return null;
		if (notUnderstood) return <NotUnderstood words={parsed.unknown.join(' ')} onTry={(w) => setQuery(w)} />;
		const ctx: PlayContext = { from: 'search', stack: null, q: query, sort };
		const chips = parsed.words.filter((w) => w.key !== 'sort');
		const tokens = filterTokens(parsed.filter);
		return (
			<>
				{chips.length > 0 && (
					<ul className="applied" aria-label="Applied filters">
						{chips.map((w) => {
							const label = chipLabel(w, tokens);
							return (
								<li key={`${w.key}:${w.source}`} className="chip filter">
									<span>
										<b>{label.group}</b> {label.label}
									</span>
									<button type="button" aria-label={`Remove ${label.label}`} onClick={() => setQuery(removeFromQuery(text, w.source))}>
										{XS}
									</button>
								</li>
							);
						})}
					</ul>
				)}
				{parsed.unknown.length > 0 && (
					<p className="p-hint">
						Skipped “<b>{parsed.unknown.join(' ')}</b>”. Try a level, a day, a month or a date.
					</p>
				)}
				{/* Under the filters it would keep; never for a search that finds nothing (UX-055). */}
				{isFiltered(parsed.filter) &&
					!full &&
					results.length > 0 &&
					builders.trigger(
						'search',
						<button type="button" className="tlink save-search" onClick={() => newStack('search', { ...searchFilter })}>
							<Icon.plus />
							Save as a collection
						</button>,
					)}
				<div className="toolbar">
					<p className="count">
						<b>
							{results.length} {plural(results.length, 'puzzle')}
						</b>
					</p>
					{results.length > 1 && sortSelect(searchFilter.sort, (s) => navigate(playPath({ q: query, sort: s }), { replace: true }))}
				</div>
				{results.length > 0 ? (
					<Rows items={results} shown={shown} onMore={showMore} path={(it) => itemPath(it, ctx)} label="Results" missedGiven={onlyMissed(searchFilter)} />
				) : (
					<NoResults
						what={describeFilter(parsed.filter)}
						// A loosening that would leave none of your words understood would only land
						// on "Nothing found": "Clear the search" is the honest way out then.
						relax={relaxations(lists.dailies, parsed.filter).filter((r) => parsed.words.some((w) => w.key !== r.key))}
						label={(r) => relaxLabel(r.key)}
						onRelax={(r) => {
							track('archive_relaxed', { key: r.key });
							setQuery(removeKeyFromQuery(text, parsed.words, r.key));
						}}
						onClear={() => setQuery('')}
					/>
				)}
			</>
		);
	};

	// ---- An open stack ------------------------------------------------------------------
	const stackTitle = (s: StackDef, heading: 'h1' | 'h2') => {
		const H = heading;
		// Phone: the page's title. Desktop: a section beside the rail, under Play's title (UX-002).
		return (
			<H className={heading === 'h1' ? 'page-title stack-title' : 'h-section stack-title'} id="stack-title" tabIndex={-1}>
				{s.name}
			</H>
		);
	};
	const stackView = (s: StackDef, heading: 'h1' | 'h2') => {
		const list = stackList;
		const top = nextInStack(list);
		const tokens = filterTokens(s.filter);
		const ctx: PlayContext = { from: 'stack', stack: s.id, q: null, sort };
		return (
			<section className={`stack-view${top ? ' has-top' : ''}${s.special ? ' special' : ''}`} aria-labelledby="stack-title">
				{s.special ? <SpecialPoster type={s.special} name={s.name} sub={s.sub} items={list} heading={heading} /> : stackTitle(s, heading)}
				{/* A built-in's line says what it is; yours shows its rules, as facts, beside Edit (UX-053). */}
				{!s.mine && !s.special && s.sub && <p className="page-lede stack-lede">{s.sub}</p>}
				{s.mine && (
					<div className="rules-row">
						<ul className="rules" aria-label="This collection’s rules">
							{tokens.length ? (
								tokens.map((t) => (
									<li key={`${t.key}:${t.label}`} className="chip info">
										{t.group} <b>{t.label}</b>
									</li>
								))
							) : (
								<li className="chip info">Every puzzle</li>
							)}
						</ul>
						{builders.trigger(
							`edit:${s.id}`,
							<button type="button" className="btn sm secondary" onClick={() => editStack(s)} aria-label={`Edit ${s.name}`}>
								<Icon.sliders />
								Edit
							</button>,
						)}
					</div>
				)}
				<div className="toolbar">
					<p className="count">
						<b>
							{list.length} {plural(list.length, 'puzzle')}
						</b>
					</p>
					{list.length > 1 && sortSelect(sort ?? s.filter.sort, (v) => navigate(playPath({ stack: s.id, sort: v === s.filter.sort ? null : v }), { replace: true }))}
				</div>
				{list.length > 0 ? (
					<Rows
						items={list}
						shown={shown}
						onMore={showMore}
						path={(it) => itemPath(it, ctx)}
						onOpen={(it, i) => track('stack_played', { ...stackEvent(s), position: i + 1, ...(it.daysAgo !== null ? { daysAgo: it.daysAgo } : {}) })}
						label={s.name}
						missedGiven={onlyMissed(s.filter)}
						levelFirst={!!s.special}
					/>
				) : (
					<div className="empty quiet">
						<p className="empty-line">{s.id === 'missed' ? 'All caught up.' : s.id === 'progress' ? 'Nothing on the go.' : s.mine ? 'Nothing matches these rules yet.' : 'Nothing left to play here.'}</p>
					</div>
				)}
				{top && (
					<div className="playtop">
						<button type="button" className={`btn md primary${wide ? ' fit' : ''}`} onClick={() => playFrom(s, list, top, sort)}>
							Play {s.special ? LEVEL_NAME[top.level] : itemName(top)}
							<Wedge direction="right" />
						</button>
					</div>
				)}
			</section>
		);
	};

	// What the live region says: how many a search or an open stack holds, or a number or
	// date's answer in a line (one status, mounted from the start, so the first count after
	// typing is read too: UX-054, QA4-10).
	const goneLine = 'That collection isn’t on this device. Make it again with New collection.';
	const liveCount = (() => {
		if (!lists) return '';
		if (gone && !query && !openStack) return goneLine;
		if (!query) return shownStack ? `${stackList.length} ${plural(stackList.length, 'puzzle')}` : '';
		if (parsed.kind === 'words') return notUnderstood ? `Nothing found for “${parsed.unknown.join(' ')}”` : `${results.length} ${plural(results.length, 'puzzle')}`;
		if (lookup && (parsed.kind === 'number' || parsed.kind === 'date')) return lookupLine(lookup, askedWords(parsed), todayNumber);
		return '';
	})();
	const live = (
		<p className="sr-only" role="status">
			{liveCount}
		</p>
	);

	// ---- The stacks ----------------------------------------------------------------------
	// An empty built-in stack is hidden (it fills itself as days pass); one you made stays.
	const hasTile = (s: StackDef) => !!s.mine || (counts.get(s.id)?.length ?? 0) > 0;
	const noPastDays = (counts.get('everything')?.length ?? 0) === 0;
	// Three groups (docs/mockups/collections-specials.html): the specials we made, Sparrow's
	// own collections, then yours with New collection.
	const tile = (s: StackDef) => {
		const list = counts.get(s.id) ?? [];
		const top = nextInStack(list);
		if (s.special)
			return (
				<SpecialTile
					key={s.id}
					id={`stk-${s.id}`}
					type={s.special}
					name={s.name}
					items={list}
					top={top}
					href={playPath({ stack: s.id })}
					onOpen={() => openIt(s)}
					onPlay={() => playFrom(s, list, top, null)}
				/>
			);
		return (
			<StackTile
				key={s.id}
				id={`stk-${s.id}`}
				mine={!!s.mine}
				name={s.name}
				sub={s.sub}
				count={list.length}
				top={top}
				fan={list.slice(0, 3)}
				href={playPath({ stack: s.id })}
				onOpen={() => openIt(s)}
				onPlay={() => playFrom(s, list, top, null)}
			/>
		);
	};
	const shownStacks = stacks.filter(hasTile);
	const specials = shownStacks.filter((s) => s.special);
	// Specials' heading is for screen readers: the poster says "Special" itself.
	const group = (id: string, title: string, list: StackDef[], tail?: ReactNode, hidden = false) => (
		<>
			<h2 className={hidden ? 'sr-only' : 'h-group stk-group'} id={id}>
				{title}
			</h2>
			<ul className="stacks" aria-labelledby={id}>
				{list.map(tile)}
				{tail}
			</ul>
		</>
	);
	const tiles = (
		<section className="stk-groups" aria-label="Collections">
			{specials.length > 0 && group('stk-specials', 'Specials', specials, undefined, true)}
			{group('stk-sparrows', 'Sparrow’s', shownStacks.filter((s) => !s.special && !s.mine))}
			{group('stk-yours', 'Yours', shownStacks.filter((s) => s.mine), <NewStackTile full={full} trigger={(button) => builders.trigger('tile', button)} onOpen={() => newStack('tile')} />)}
		</section>
	);
	const railItem = (s: StackDef) => {
		const n = counts.get(s.id)?.length ?? 0;
		const on = !query && deskStack?.id === s.id;
		return (
			<li key={s.id} data-type={s.special}>
				<a
					id={`stk-${s.id}`}
					href={playPath({ stack: s.id })}
					className={n ? undefined : 'empty'}
					aria-current={on ? 'true' : undefined}
					onClick={(e) => {
						if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
						e.preventDefault();
						if (query) setText('');
						openIt(s);
					}}
				>
					<b>
						{s.special && <SeasonIcon type={s.special} />}
						{s.name}
					</b>{' '}
					<span>
						{n}
						<span className="sr-only"> {plural(n, 'puzzle')}</span>
					</span>
				</a>
			</li>
		);
	};
	const rail = (
		<nav className="rail" aria-label="Collections">
			{specials.length > 0 && (
				<>
					<h2 className="h-group" id="rail-specials">
						Specials
					</h2>
					<ul aria-labelledby="rail-specials">{specials.map(railItem)}</ul>
				</>
			)}
			<h2 className="h-group" id="rail-sparrows">
				Sparrow’s
			</h2>
			<ul aria-labelledby="rail-sparrows">{stacks.filter((s) => !s.mine && !s.special && (hasTile(s) || deskStack?.id === s.id)).map(railItem)}</ul>
			<h2 className="h-group" id="rail-mine">
				Yours
			</h2>
			<ul aria-labelledby="rail-mine">
				{mine.map(railItem)}
				<li>
					{full ? (
						<p className="rail-full">You can keep 12 collections. Delete one to make another.</p>
					) : (
						builders.trigger(
							'rail',
							<button type="button" className="rail-new" onClick={() => newStack('rail')}>
								<Icon.plus />
								New collection
							</button>,
						)
					)}
				</li>
			</ul>
		</nav>
	);

	// Loading: the real page with quiet placeholders where the list will be, only if the wait
	// is noticeable (UX-015). Failing: the error inside the page, the tab bar still there.
	const loadingOrError = (shape: 'tiles' | 'rows') =>
		data.status === 'loading' ? (
			<Pending label="Loading">
				{shape === 'tiles' ? (
					<div className="stacks stacks-skel" aria-hidden="true">
						{[0, 1, 2, 3].map((i) => (
							<Skeleton key={i} className="stk-skel" />
						))}
					</div>
				) : (
					<SkeletonRows count={5} />
				)}
			</Pending>
		) : data.status === 'error' ? (
			<ErrorState error={data.error} what="past Sparrows" plural onRetry={() => setAttempt((n) => n + 1)} inPage game screen="play" />
		) : null;

	// Phone, a stack open: its own page, "‹ Play" back to the stacks.
	if (!wide && openStack && !query) {
		const waiting = loadingOrError('rows');
		return (
			<div className={`page play play-stack${openStack.special ? ' special' : ''}`}>
				{/* The masthead is the page's banner, outside <main> (QA4-13); both scroll together. */}
				<div className="page-body page-scroll" ref={scroller} onScroll={onScroll} onClickCapture={onRowClick}>
					<header className={openStack.special ? 'stack-mast on-night' : 'stack-mast'} data-type={openStack.special}>
						<BackPill to="/play" />
						<AppMenu screen="play" install={{ todayKey }} />
					</header>
					<main className="page-main">
						{live}
						{waiting ? (
							<>
								{data.status === 'error' && stackTitle(openStack, 'h1')}
								{waiting}
							</>
						) : (
							stackView(openStack, 'h1')
						)}
					</main>
				</div>
				{builders.rest}
				<TabBar current="play" />
			</div>
		);
	}

	return (
		<div className="page play">
			{/* The brand row is the page's banner, outside <main> (QA4-13); both scroll together. */}
			<div className="page-body page-scroll" ref={scroller} onScroll={onScroll} onClickCapture={onRowClick}>
				<BrandRow streak={streak} after={<AppMenu screen="play" install={{ todayKey }} />} />
				<main className="page-main">
					<h1 className="page-title" id="play-title" tabIndex={-1}>
						Play
					</h1>
					<p className="page-lede">Past Sparrows don’t count towards your streak.</p>
					{live}
					{gone && (
						<p className="play-soon play-gone" aria-hidden="true">
							{goneLine}
						</p>
					)}
					{wide ? (
						<div className="play-lay">
							<div className="play-side">
								{searchBox}
								{lists && rail}
							</div>
							<div className="play-main">{loadingOrError('rows') ?? (query ? searchView() : deskStack && stackView(deskStack, 'h2'))}</div>
						</div>
					) : (
						<>
							{searchBox}
							{!query && !loadingOrError('tiles') && lists && noPastDays && <p className="play-soon">No past days yet. Each Sparrow moves here the day after it’s out.</p>}
							{loadingOrError(query ? 'rows' : 'tiles') ?? (query ? searchView() : tiles)}
							{!query && devMode && <DevFixtures />}
						</>
					)}
				</main>
			</div>
			{builders.rest}
			<TabBar current="play" />
		</div>
	);
}

/**
 * A number or date's answer in one line, for the page's status (the card itself holds links,
 * so it isn't a live region: QA4-10). "No. 1, Sunday 25 October: 3 levels".
 */
function lookupLine(lookup: Lookup, asked: string, todayNumber: number | null): string {
	switch (lookup.kind) {
		case 'day': {
			const n = lookup.day.levels.length;
			return `No. ${lookup.day.number}, ${longDate(lookup.day.date)}: ${n} ${plural(n, 'level')}`;
		}
		case 'today':
			return lookup.number ? `No. ${lookup.number} is today’s` : 'That’s today’s';
		case 'future':
			return todayNumber ? `${asked} isn’t out yet. Today’s is No. ${todayNumber}.` : `${asked} isn’t out yet`;
		case 'before':
			return 'Sparrow starts at No. 1';
		default:
			return `Couldn’t find ${asked}`;
	}
}

/** A list of missed days only: its rows needn't each say so (QA1-5). */
function onlyMissed(filter: ArchiveFilter): boolean {
	return filter.status.length === 1 && filter.status[0] === 'missed';
}

/** Desktop's stack when none is asked for: Missed (or Quick ones), else In progress, else the first with anything. A special never: it's a choice, not where you were. */
function defaultStack(all: readonly StackDef[], counts: Map<string, ArchiveItem[]>): StackDef | null {
	const stacks = all.filter((s) => !s.special);
	const order = [stacks[1], stacks[0], ...stacks.slice(2)].filter((s): s is StackDef => !!s);
	return order.find((s) => (counts.get(s.id)?.length ?? 0) > 0) ?? stacks.find((s) => s.id === 'everything') ?? null;
}

const VALUE_NAME: Record<string, string> = {
	easy: 'Easy',
	medium: 'Medium',
	hard: 'Hard',
	new: 'Not started',
	started: 'In progress',
	done: 'Done',
	perfect: 'Perfect',
	missed: 'Missed',
};

/** A word's chip: its rule's kind and value. */
function chipLabel(w: QueryWord, tokens: readonly Token[]): { group: string; label: string } {
	const same = tokens.filter((t) => t.key === w.key);
	if (w.key === 'levels' || w.key === 'status') {
		const label = w.value
			.split(',')
			.map((v) => VALUE_NAME[v] ?? v)
			.join(' or ');
		return { group: same[0]?.group ?? '', label };
	}
	return same[0] ?? { group: '', label: w.source };
}

/**
 * Dev mode: the bundled fixtures, then the api's dev puzzles (puzzles/dev/, ids `dev-…`,
 * which need the dev-tools token in production), played by id (lazily loaded chunks).
 */
function DevFixtures() {
	const [items, setItems] = useState<ArchiveItem[]>([]);
	// Why the api's dev puzzles aren't listed, when they aren't (no token, or the call failed).
	const [note, setNote] = useState('');
	useEffect(() => {
		const ctrl = new AbortController();
		const row = (p: Puzzle) => ({ id: p.id, title: p.title, level: p.level ?? ('medium' as const), difficulty: p.difficulty, width: p.width, height: p.height, shape: puzzleShape(p) });
		const show = (list: ReturnType<typeof row>[]) =>
			setItems(practiceItems(list, { stats: { version: 2, days: {}, practice: { completed: 0, elapsedMs: 0, checksUsed: 0, revealsUsed: 0 } }, progress: {} }));
		void Promise.all([import('../dev/fixtures.ts'), import('../dev/api.ts')]).then(async ([m, api]) => {
			const bundled = m.fixtureSummaries.flatMap((f) => {
				const p = m.fixturePuzzle(f.id);
				return p ? [row(p)] : [];
			});
			show(bundled);
			try {
				const ids = (await api.fetchDevPuzzles(ctrl.signal)).map((p) => p.id).filter((id) => id.startsWith('dev-'));
				const served = await Promise.all(ids.map((id) => api.fetchDevPuzzle(id, ctrl.signal)));
				if (ctrl.signal.aborted) return;
				if (served.length) show([...bundled, ...served.map(row)]);
			} catch {
				if (!ctrl.signal.aborted) setNote('Couldn’t load the dev puzzles from the api.');
			}
		});
		return () => ctrl.abort();
	}, []);
	if (!items.length) return null;
	return (
		<>
			<h2 className="h-group">Dev fixtures (dev mode)</h2>
			<Rows items={items} shown={items.length} onMore={() => undefined} path={(it) => `/play/${encodeURIComponent(it.puzzleId)}`} label="Dev fixtures" />
			{note && <p className="quiet">{note}</p>}
		</>
	);
}
